'use client';

import { useMemo, useState } from 'react';
import { products, sortProductsByCatalogOrder } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { ProductsSEO } from '@/seo/ProductsSEO';
import { IconSearch } from '@/components/Icons/Icons';
import { ProductsBondexFiyatlariContent } from '@/views/content/ProductsBondexFiyatlariContent';
import styles from './Products.module.css';

type CategoryFilter = 'all' | 'Bondex Sünger' | 'Bariyerli Bondex' | 'Çift Bariyerli Bondex' | 'Akustik Sünger';
type SortOption = 'default' | 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc';
type DensityFilter = 'all' | '120' | '150' | '180';

function getDensityOptionsByCategory(_category: string): number[] {
  return [120, 150, 180];
}

export function Products() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [brand, setBrand] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('default');
  const [density, setDensity] = useState<DensityFilter>('all');

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const densityValue = density === 'all' ? null : Number(density);

    let list = products.filter((p) => {
      const catOk = category === 'all' ? true : p.category === category;
      if (!catOk) return false;
      if (!brand) return false;

      if (densityValue) {
        const allowed = getDensityOptionsByCategory(p.category);
        if (!allowed.includes(densityValue)) return false;
      }

      if (!qq) return true;
      const hay = `${p.name} ${p.shortDescription} ${p.category} ${p.description}`.toLowerCase();
      return hay.includes(qq);
    });

    if (sortBy === 'default') {
      list = sortProductsByCatalogOrder(list);
    } else {
      list = [...list].sort((a, b) => {
        if (sortBy === 'name-asc') return a.name.localeCompare(b.name, 'tr');
        if (sortBy === 'name-desc') return b.name.localeCompare(a.name, 'tr');

        const ap = a.priceTRY ?? Number.MAX_SAFE_INTEGER;
        const bp = b.priceTRY ?? Number.MAX_SAFE_INTEGER;
        if (sortBy === 'price-asc') return ap - bp;
        return bp - ap;
      });
    }

    return list;
  }, [q, category, brand, sortBy, density]);

  const resetFilters = () => {
    setCategory('all');
    setBrand(true);
    setSortBy('default');
    setDensity('all');
    setQ('');
  };

  return (
    <main className={styles.wrap}>
      <ProductsSEO />
      <section className={styles.page}>
        <div className="container">
          <div className={styles.layout}>
            <aside className={styles.sidebar}>
              <p className={styles.sidebarTitle}>Filtre</p>

              <div className={styles.filterBlock}>
                <p className={styles.filterLabel}>Kategoriler</p>
                <label className={styles.filterItem}>
                  <input type="radio" checked={category === 'all'} onChange={() => setCategory('all')} />
                  Tüm Ürünler
                </label>
                <label className={styles.filterItem}>
                  <input type="radio" checked={category === 'Bondex Sünger'} onChange={() => setCategory('Bondex Sünger')} />
                  Bondex Sünger
                </label>
                <label className={styles.filterItem}>
                  <input
                    type="radio"
                    checked={category === 'Bariyerli Bondex'}
                    onChange={() => setCategory('Bariyerli Bondex')}
                  />
                  Bariyerli Bondex Sünger
                </label>
                <label className={styles.filterItem}>
                  <input
                    type="radio"
                    checked={category === 'Çift Bariyerli Bondex'}
                    onChange={() => setCategory('Çift Bariyerli Bondex')}
                  />
                  Çift Bariyerli Bondex Sünger
                </label>
                <label className={styles.filterItem}>
                  <input
                    type="radio"
                    checked={category === 'Akustik Sünger'}
                    onChange={() => setCategory('Akustik Sünger')}
                  />
                  Akustik Süngerler
                </label>
              </div>

              <div className={styles.filterBlock}>
                <p className={styles.filterLabel}>Markalar</p>
                <label className={styles.filterItem}>
                  <input type="checkbox" checked={brand} onChange={(e) => setBrand(e.target.checked)} />
                  Echopan
                </label>
              </div>

              <button type="button" className={styles.resetBtn} onClick={resetFilters}>
                Temizle
              </button>
            </aside>

            <section className={styles.content}>
              <header className={styles.contentTop}>
                <h1 className={styles.title}>Bondex Sünger Fiyatları</h1>
                <div className={styles.topControls}>
                  <div className={styles.searchWrapper}>
                    <IconSearch className={styles.searchIcon} />
                    <input
                      className={styles.search}
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Ürün Ara"
                      aria-label="Ürün Ara"
                    />
                  </div>
                  <select className={styles.select} value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)}>
                    <option value="default">Varsayılan sıralama</option>
                    <option value="name-asc">İsim A-Z</option>
                    <option value="name-desc">İsim Z-A</option>
                    <option value="price-asc">Fiyat düşük-yüksek</option>
                    <option value="price-desc">Fiyat yüksek-düşük</option>
                  </select>
                  <select className={styles.select} value={density} onChange={(e) => setDensity(e.target.value as DensityFilter)}>
                    <option value="all">Yoğunluk oranı (Tümü)</option>
                    <option value="120">120 dns</option>
                    <option value="150">150 dns</option>
                    <option value="180">180 dns</option>
                  </select>
                </div>
              </header>

              <h2 className={styles.srOnly}>Ürün Listesi</h2>
              <p className={styles.foundText}>{filtered.length} ürün bulundu</p>

              {filtered.length > 0 ? (
                <div className={`${styles.grid} ${filtered.length === 1 ? styles.gridSingle : ''}`}>
                  {filtered.map((p) => (
                    <div key={p.slug} className={styles.cardWrap}>
                      <ProductCard product={p} variant="catalog" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <h2 className={styles.emptyTitle}>Ürün bulunamadı</h2>
                  <p className={styles.emptyText}>Arama veya filtre seçeneklerini güncelleyip tekrar deneyin.</p>
                </div>
              )}

              <ProductsBondexFiyatlariContent />
            </section>
          </div>
        </div>
      </section>
    </main>
  );
}
